import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { DeviceSipPanel } from "./DeviceSipPanel";
import { isSoftphoneEquipment } from "./equipment-kind";
import {
  DeviceConnections,
  NumberConnection,
  LineRegistration,
  type ConnectionState,
  type ConnectionFocus,
} from "./AccountConnections";
import {
  DeviceProvisioningPanel,
  ProvisioningServerStatus,
} from "./DeviceProvisioningPanel";
import {
  EquipmentModelFields,
  blankEquipmentSelection,
} from "./EquipmentModelFields";
import {
  NumberPurchasePanel,
  type PurchaseLocation,
} from "./NumberPurchasePanel";

type Row = Record<string, any>;
type Api = <T>(
  path: string,
  options?: {
    method?: string;
    body?: unknown;
    csrf?: string | undefined;
    signal?: AbortSignal;
  },
) => Promise<T>;
type Props = {
  api: Api;
  csrf?: string | undefined;
  readOnly?: boolean | undefined;
};
type AccountProps = Props & {
  accountId: string;
  customer: boolean;
  onSaved: () => Promise<void>;
};
const labels: Record<string, string> = {
  received: "Awaiting review",
  needs_information: "Needs information",
  ready: "Ready for carrier submission",
  cancelled: "Cancelled",
  inventory: "In equipment inventory",
  sip_phone: "SIP phone",
  ata: "Analog telephone adapter",
  gateway: "Gateway",
  other: "Other equipment",
};
const label = (value: string) => labels[value] ?? value.replaceAll("_", " ");
const numberList = (value: FormDataEntryValue | undefined) =>
  String(value ?? "")
    .split(/[\n,;]+/)
    .map((item) => item.trim())
    .filter(Boolean);
function Field({ title, children }: { title: string; children: ReactNode }) {
  return (
    <label className="cr-field">
      <span>{title}</span>
      {children}
    </label>
  );
}
function Feedback({
  error = false,
  children,
}: {
  error?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={`cr-notice ${error ? "error" : ""}`}
      role={error ? "alert" : "status"}
    >
      {children}
    </div>
  );
}
function MutationForm({
  api,
  csrf,
  path,
  children,
  button,
  success,
  onSaved,
  transform = (value) => value,
  reset = true,
  onReset,
  readOnly = false,
}: Props & {
  path: string;
  children: ReactNode;
  button: string;
  success: string;
  onSaved: () => Promise<void>;
  transform?: (value: Record<string, FormDataEntryValue>) => unknown;
  reset?: boolean;
  onReset?: (() => void) | undefined;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const command = useRef({ hash: "", key: crypto.randomUUID() });
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (readOnly) return;
    const form = event.currentTarget;
    const value = transform(Object.fromEntries(new FormData(form)));
    const hash = JSON.stringify({ path, value });
    if (command.current.hash !== hash)
      command.current = { hash, key: crypto.randomUUID() };
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await api(path, {
        method: "POST",
        csrf,
        body: { ...(value as object), requestKey: command.current.key },
      });
      command.current = { hash: "", key: crypto.randomUUID() };
      if (reset) form.reset();
      setNotice(success);
      try {
        await onSaved();
      } catch {
        setError(
          "Saved, but the updated records could not be loaded. Refresh the account before making another request.",
        );
      }
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  if (readOnly)
    return (
      <p className="cr-form-note">
        Read-only customer view. Exit to make changes as staff.
      </p>
    );
  return (
    <form className="cr-form" onSubmit={submit} onReset={onReset}>
      <fieldset className="cr-form-fields" disabled={busy}>
        {children}
        <button className="cr-button">{busy ? "Saving…" : button}</button>
      </fieldset>
      {error && <Feedback error>{error}</Feedback>}
      {notice && <Feedback>{notice}</Feedback>}
    </form>
  );
}
function AddressFields({ address = {} }: { address?: Row }) {
  return (
    <div className="cr-form-grid">
      <Field title="Street address">
        <input
          name="line1"
          required
          maxLength={200}
          autoComplete="address-line1"
          defaultValue={address.line1}
        />
      </Field>
      <Field title="Apartment, suite or unit (optional)">
        <input
          name="line2"
          maxLength={120}
          autoComplete="address-line2"
          defaultValue={address.line2}
        />
      </Field>
      <Field title="City">
        <input
          name="city"
          required
          maxLength={100}
          autoComplete="address-level2"
          defaultValue={address.city}
        />
      </Field>
      <Field title="State / region">
        <input
          name="region"
          required
          maxLength={80}
          autoComplete="address-level1"
          defaultValue={address.region}
        />
      </Field>
      <Field title="Postal code">
        <input
          name="postalCode"
          required
          maxLength={20}
          autoComplete="postal-code"
          defaultValue={address.postalCode}
        />
      </Field>
    </div>
  );
}
export function LocationForm(props: AccountProps) {
  return (
    <details className="cr-panel cr-editor">
      <summary>Add a service location</summary>
      <MutationForm
        {...props}
        path={`/admin/accounts/${props.accountId}/locations`}
        button="Save location"
        success="Location recorded. E911 validation is still required."
      >
        <Field title="Location name">
          <input name="name" required maxLength={120} />
        </Field>
        <AddressFields />
        <p className="cr-form-note">
          Saving an address does not register it for emergency calling.
        </p>
      </MutationForm>
    </details>
  );
}
export function LocationCorrectionForm(
  props: AccountProps & { location: Row },
) {
  if (props.customer || props.readOnly) return null;
  const version = Number(props.location.version);
  if (!Number.isSafeInteger(version) || version < 1)
    return (
      <Feedback error>
        Reload the account before correcting this location.
      </Feedback>
    );
  return (
    <details className="cr-editor">
      <summary>Correct this location</summary>
      <MutationForm
        key={`${props.location.id}:${version}`}
        {...props}
        path={`/admin/accounts/${props.accountId}/locations/${props.location.id}`}
        button="Save location correction"
        success="Location record saved. Address changes require new E911 verification; no carrier update was sent."
        reset={false}
        transform={(value) => ({
          expectedVersion: version,
          name: value.name,
          address: {
            line1: value.line1,
            line2: value.line2,
            city: value.city,
            region: value.region,
            postalCode: value.postalCode,
          },
        })}
      >
        <Field title="Location name">
          <input
            name="name"
            required
            maxLength={120}
            defaultValue={props.location.name}
          />
        </Field>
        <AddressFields address={props.location.address} />
        <p className="cr-form-note">
          Use the physical service address. Changing it clears the previous
          address validation for every device using this location. It does not
          update emergency-call routing at the carrier.
        </p>
      </MutationForm>
    </details>
  );
}

function DeviceLocationEditor(
  props: AccountProps & { device: Row; locations: Row[] },
) {
  if (props.customer || props.readOnly) return null;
  const version = Number(props.device.version);
  if (!Number.isSafeInteger(version) || version < 1)
    return (
      <Feedback error>
        Reload this account before correcting the equipment location.
      </Feedback>
    );
  return (
    <details className="cr-editor">
      <summary>Correct location</summary>
      <MutationForm
        key={`${props.device.id}:${version}`}
        {...props}
        path={`/admin/accounts/${props.accountId}/equipment/${props.device.id}/location`}
        button="Save equipment location"
        success="Equipment location saved. Live E911 verification is still required; phone settings and routing were not changed."
        reset={false}
        transform={(value) => ({
          expectedVersion: version,
          locationId: value.locationId || null,
        })}
      >
        <Field
          title={`Physical service location for ${props.device.label || props.device.model}`}
        >
          <select
            name="locationId"
            defaultValue={props.device.location_id ?? ""}
          >
            <option value="">Not assigned</option>
            {props.locations.map((location) => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </select>
        </Field>
        {!props.locations.length && (
          <p>Add a service location from the account overview first.</p>
        )}
        <p className="cr-form-note">
          This records where the device is physically used. It does not verify
          E911, send provisioning, move a phone number, or change stored SIP
          credentials.
        </p>
      </MutationForm>
    </details>
  );
}

export function EquipmentPanel(
  props: AccountProps & {
    devices: Row[];
    locations: Row[];
    onConnections?: () => void;
    connections?: ReactNode;
    connectionState?: ConnectionState;
    onAssignment?: (focus: ConnectionFocus) => void;
  },
) {
  const path = `/${props.customer ? "customer" : "admin"}/accounts/${props.accountId}/equipment`;
  const [selection, setSelection] = useState(blankEquipmentSelection);
  useEffect(
    () => setSelection(blankEquipmentSelection),
    [props.accountId, props.customer],
  );
  return (
    <>
      <ProvisioningServerStatus api={props.api} customer={props.customer} />
      {props.onConnections && (
        <div className="cr-actions">
          <button className="cr-button secondary" onClick={props.onConnections}>
            {props.customer || props.readOnly
              ? "View number and extension connections"
              : "Assign numbers and extensions"}
          </button>
        </div>
      )}
      <details className="cr-panel cr-editor">
        <summary>Add equipment</summary>
        <MutationForm
          {...props}
          path={path}
          button="Register equipment"
          success="Equipment registered. Phone configuration has not been sent."
          onReset={() => setSelection(blankEquipmentSelection)}
        >
          <div className="cr-form-grid cr-equipment-fields">
            <EquipmentModelFields
              selection={selection}
              onChange={setSelection}
            />
            <Field title="MAC address">
              <input
                name="hardwareId"
                required
                maxLength={17}
                autoCapitalize="characters"
                spellCheck={false}
                placeholder="12 hexadecimal digits"
              />
            </Field>
            <Field title="Equipment label (optional)">
              <input name="label" maxLength={120} />
            </Field>
            <Field title="Serial number (optional)">
              <input name="serialNumber" maxLength={120} />
            </Field>
            <Field title="Service location">
              <select
                name="locationId"
                defaultValue={
                  props.locations.length === 1 ? props.locations[0]!.id : ""
                }
              >
                <option value="">Not assigned yet</option>
                {props.locations.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <p className="cr-form-note">
            Use the MAC address printed on the device. The selected model is
            saved for provisioning. Open Automatic setup below for this device's
            current setup status and instructions.
          </p>
        </MutationForm>
      </details>
      {props.devices.length ? (
        <div
          className="cr-table-wrap"
          tabIndex={0}
          aria-label="Equipment records"
        >
          <table>
            <thead>
              <tr>
                <th>Equipment</th>
                <th>MAC address</th>
                <th>Numbers / extensions</th>
                <th>Location</th>
                <th>Recorded state</th>
                <th>Setup</th>
              </tr>
            </thead>
            <tbody>
              {props.devices.map((row) => (
                <tr key={row.id}>
                  <td>
                    <strong>{row.label || row.model}</strong>
                    <br />
                    {row.label ? row.model : label(row.equipment_kind)}
                    {row.serial_number && (
                      <small className="cr-record-meta">
                        Serial: {row.serial_number}
                      </small>
                    )}
                  </td>
                  <td>
                    {isSoftphoneEquipment(row)
                      ? "Logical softphone"
                      : row.hardware_id.match(/.{2}/g)?.join(":")}
                  </td>
                  <td>
                    {props.connectionState ? (
                      <DeviceConnections
                        state={props.connectionState}
                        deviceId={String(row.id)}
                        onEdit={
                          !props.customer && !props.readOnly
                            ? props.onAssignment
                            : undefined
                        }
                      />
                    ) : (
                      "Connection details unavailable"
                    )}
                  </td>
                  <td>
                    {props.locations.find(
                      (place) => place.id === row.location_id,
                    )?.name ?? "Not assigned"}
                    <DeviceLocationEditor
                      {...props}
                      device={row}
                      locations={props.locations}
                    />
                  </td>
                  <td>{label(row.status)}</td>
                  <td>
                    {isSoftphoneEquipment(row)
                      ? "Manual softphone setup"
                      : row.provisioning?.modelReviewRequired === false
                        ? "Model identified"
                        : "Model needs review"}
                    <small className="cr-record-meta">
                      {isSoftphoneEquipment(row)
                        ? "Open Manual phone setup below"
                        : "Open Automatic setup below"}
                    </small>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="cr-empty">
          No equipment registered. Add a phone, ATA, or gateway above.
        </p>
      )}
      {props.connections}
      {props.devices.map((row) => (
        <section
          key={row.id}
          className="cr-device-setup"
          aria-label={`${row.label || row.model} setup`}
        >
          <h3>{row.label || row.model}</h3>
          {!isSoftphoneEquipment(row) && (
            <DeviceProvisioningPanel
              key={`provisioning-${row.id}`}
              {...props}
              deviceId={row.id}
              deviceName={row.label || row.model}
            />
          )}
          <DeviceSipPanel
            key={row.id}
            {...props}
            deviceId={row.id}
            deviceName={row.label || row.model}
          />
        </section>
      ))}
    </>
  );
}
export function InventoryForm(props: Props & { onSaved: () => Promise<void> }) {
  return (
    <details className="cr-panel cr-editor">
      <summary>Add verified number inventory</summary>
      <MutationForm
        {...props}
        path="/admin/number-inventory"
        button="Add numbers to inventory"
        success="Inventory saved. Account holders can now select these numbers."
        transform={(value) => ({
          ...value,
          numbers: numberList(value.numbers),
          confirmed: value.confirmed === "on",
        })}
      >
        <Field title="Telephone numbers, one per line (up to 100)">
          <textarea
            name="numbers"
            required
            rows={5}
            maxLength={2500}
            placeholder="Include the country code"
          />
        </Field>
        <div className="cr-form-grid">
          <Field title="Carrier">
            <input name="carrier" value="Twilio Main" readOnly />
          </Field>
          <Field title="Carrier inventory / order reference">
            <input name="sourceReference" required maxLength={200} />
          </Field>
          <Field title="Rate center (optional)">
            <input name="rateCenter" maxLength={100} />
          </Field>
          <Field title="State / region (optional)">
            <input name="region" maxLength={80} />
          </Field>
        </div>
        <label className="cr-check">
          <input name="confirmed" type="checkbox" required />
          <span>
            I verified that Cache River controls these numbers and that they are
            available for assignment.
          </span>
        </label>
        <p className="cr-form-note">
          This records existing carrier inventory. It does not purchase numbers
          or check carrier availability.
        </p>
      </MutationForm>
    </details>
  );
}
function PortFields({ port }: { port?: Row }) {
  return (
    <>
      <Field
        title={
          port
            ? "Telephone number"
            : "Numbers to transfer, one per line (up to 25)"
        }
      >
        <textarea
          name="numbers"
          required
          rows={port ? 1 : 4}
          maxLength={650}
          defaultValue={port?.requested_e164}
          readOnly={Boolean(port)}
          placeholder="Include the country code"
        />
      </Field>
      <div className="cr-form-grid">
        <Field title="Authorized account holder">
          <input
            name="authorizedName"
            required
            maxLength={160}
            defaultValue={port?.authorized_name}
          />
        </Field>
        <Field title="Current telephone carrier">
          <input
            name="losingCarrier"
            required
            maxLength={120}
            defaultValue={port?.losing_carrier}
          />
        </Field>
        <Field title="Billing telephone number">
          <input
            name="billingTelephone"
            type="tel"
            required
            maxLength={30}
            defaultValue={port?.billing_telephone}
          />
        </Field>
        <Field title="Requested transfer date (optional)">
          <input
            name="requestedDate"
            type="date"
            defaultValue={port?.requested_date?.slice(0, 10)}
          />
        </Field>
      </div>
      <h4>Service address on the current carrier account</h4>
      <AddressFields address={port?.service_address} />
      <Field title="Notes (optional)">
        <textarea
          name="notes"
          rows={3}
          maxLength={2000}
          defaultValue={port?.notes}
        />
      </Field>
      <p className="cr-form-note">
        Do not enter account passwords, transfer PINs, or payment details. Staff
        will arrange any required documents and carrier authorization
        separately. A requested date is not a confirmed transfer date. Keep your
        existing service active until the transfer is confirmed.
      </p>
      <label className="cr-check">
        <input name="authorized" type="checkbox" required />
        <span>
          I am authorized to request these transfers and have checked the
          account details.
        </span>
      </label>
    </>
  );
}
type NumberPage = { items: Row[]; nextCursor: string | null };
function parseNumberRecords(raw: unknown, inventory: boolean): NumberPage {
  const value = raw as NumberPage | null;
  if (
    !value ||
    !Array.isArray(value.items) ||
    value.items.some(
      (row) =>
        !row ||
        typeof row !== "object" ||
        typeof row.id !== "string" ||
        (inventory
          ? typeof row.e164 !== "string"
          : ["requested_e164", "status", "review_status"].some(
              (field) => typeof row[field] !== "string",
            )),
    ) ||
    (inventory &&
      value.nextCursor !== null &&
      typeof value.nextCursor !== "string")
  )
    throw new Error(
      `${inventory ? "Owned inventory" : "Port requests"} returned incomplete records. Refresh to try again.`,
    );
  return {
    items: value.items,
    nextCursor: inventory ? value.nextCursor : null,
  };
}
async function readNumberRecords(
  api: Api,
  path: string,
  signal: AbortSignal,
): Promise<unknown> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let cancel = () => {};
  const interrupted = new Promise<never>((_resolve, reject) => {
    cancel = () => {
      controller.abort();
      reject(new Error("Number records request canceled."));
    };
    if (signal.aborted) cancel();
    else {
      signal.addEventListener("abort", cancel, { once: true });
      timer = setTimeout(() => {
        controller.abort();
        reject(
          new Error("The records request timed out. Use Retry to check again."),
        );
      }, 10_000);
    }
  });
  try {
    return await Promise.race([
      Promise.resolve().then(() => {
        if (controller.signal.aborted)
          throw new Error("Number records request canceled.");
        return api<unknown>(path, { signal: controller.signal });
      }),
      interrupted,
    ]);
  } finally {
    clearTimeout(timer);
    signal.removeEventListener("abort", cancel);
    controller.abort();
  }
}
function useNumberRecords(
  api: Api,
  path: string,
  refreshVersion: number,
  inventory: boolean,
) {
  const [state, setState] = useState<{
    api: Api;
    path: string;
    data: NumberPage | null;
    error: string;
    loading: boolean;
  } | null>(null);
  const generation = useRef(0),
    request = useRef<AbortController | null>(null);
  const load = useCallback(
    async (pagePath = path, append = false) => {
      const current = ++generation.current;
      request.current?.abort();
      const controller = new AbortController();
      request.current = controller;
      setState((previous) => ({
        api,
        path,
        data:
          previous?.api === api && previous.path === path
            ? previous.data
            : null,
        error: "",
        loading: true,
      }));
      try {
        const result = parseNumberRecords(
          await readNumberRecords(api, pagePath, controller.signal),
          inventory,
        );
        if (current !== generation.current || controller.signal.aborted) return;
        setState((previous) => ({
          api,
          path,
          data:
            append &&
            previous?.api === api &&
            previous.path === path &&
            previous.data
              ? {
                  items: [...previous.data.items, ...result.items],
                  nextCursor: result.nextCursor,
                }
              : result,
          error: "",
          loading: false,
        }));
      } catch (cause) {
        if (current === generation.current && !controller.signal.aborted)
          setState((previous) => ({
            api,
            path,
            data:
              previous?.api === api && previous.path === path
                ? previous.data
                : null,
            error:
              cause instanceof Error
                ? cause.message
                : "Records could not be loaded. Retry to check again.",
            loading: false,
          }));
      }
    },
    [api, path, inventory],
  );
  useEffect(() => {
    void load();
    return () => {
      ++generation.current;
      request.current?.abort();
    };
  }, [load, refreshVersion]);
  const scoped = state?.api === api && state.path === path ? state : null;
  return {
    data: scoped?.data ?? null,
    error: scoped?.error ?? "",
    loading: scoped?.loading ?? true,
    reload: () => load(),
    more: (pagePath: string) => load(pagePath, true),
  };
}
export function NumbersPanel(
  props: AccountProps & {
    numbers: Row[];
    accountName: string;
    locations: PurchaseLocation[];
    onSetup?: (() => void) | undefined;
    preferredNumber?: string | undefined;
    refreshVersion: number;
    connectionState?: ConnectionState;
    onAssignment?: (focus: ConnectionFocus) => void;
  },
) {
  const base = `/${props.customer ? "customer" : "admin"}/accounts/${props.accountId}`;
  const [areaCode, setAreaCode] = useState("");
  const [searchArea, setSearchArea] = useState("");
  const [selectedNumber, setSelectedNumber] = useState("");
  const inventory = useNumberRecords(
    props.api,
    `${base}/available-numbers${searchArea ? `?areaCode=${searchArea}` : ""}`,
    props.refreshVersion,
    true,
  );
  const portRecords = useNumberRecords(
    props.api,
    `${base}/ports`,
    props.refreshVersion,
    false,
  );
  const choices = inventory.data?.items ?? [],
    nextCursor = inventory.data?.nextCursor ?? null,
    ports = portRecords.data?.items ?? [];
  useEffect(() => {
    setAreaCode("");
    setSearchArea("");
    setSelectedNumber("");
  }, [base]);
  useEffect(() => {
    if (inventory.data)
      setSelectedNumber((selected) =>
        inventory.data!.items.some((row) => row.id === selected)
          ? selected
          : "",
      );
  }, [inventory.data]);
  async function saved() {
    await props.onSaved();
  }
  async function moreNumbers() {
    if (!nextCursor || inventory.loading) return;
    const query = new URLSearchParams({
      after: nextCursor,
      ...(searchArea ? { areaCode: searchArea } : {}),
    });
    await inventory.more(`${base}/available-numbers?${query}`);
  }
  const transformPort = (value: Record<string, FormDataEntryValue>) => ({
    ...value,
    numbers: numberList(value.numbers),
    authorized: value.authorized === "on",
  });
  return (
    <>
      <h3>Account numbers</h3>
      {props.numbers.length ? (
        <div
          className="cr-table-wrap"
          tabIndex={0}
          aria-label="Account telephone numbers"
        >
          <table>
            <thead>
              <tr>
                <th>Telephone number</th>
                <th>Rings at</th>
                <th>Account record</th>
                <th>Phone status</th>
                <th>E911</th>
              </tr>
            </thead>
            <tbody>
              {props.numbers.map((row) => (
                <tr key={row.id}>
                  <td>{row.e164}</td>
                  <td>
                    {props.connectionState ? (
                      <NumberConnection
                        state={props.connectionState}
                        numberId={String(row.id)}
                        onEdit={
                          !props.customer && !props.readOnly
                            ? props.onAssignment
                            : undefined
                        }
                      />
                    ) : (
                      "Connection details unavailable"
                    )}
                  </td>
                  <td>
                    {row.portal_linked_at
                      ? "Assigned to this account"
                      : row.status === "reserved"
                        ? "Reserved for this account"
                        : label(row.status)}
                  </td>
                  <td>
                    {(() => {
                      const state = props.connectionState;
                      const number = state?.data?.numbers.find(
                        (item) =>
                          item.id === row.id && item.number === row.e164,
                      );
                      const line = state?.data?.lines.find(
                        (item) => item.id === number?.lineId,
                      );
                      if (["released", "aging", "failed"].includes(row.status))
                        return <span>Number unavailable for calling</span>;
                      if (state?.error)
                        return <span>Live phone status unavailable</span>;
                      if (!state?.data)
                        return <span>Checking phone connection…</span>;
                      if (!number)
                        return (
                          <span>Connection not recorded for this number</span>
                        );
                      if (!number.lineId) return <span>No phone assigned</span>;
                      if (!line) return <span>Saved phone needs review</span>;
                      if (line.status === "disconnected")
                        return <span>Disconnected line</span>;
                      const device = state?.data?.devices.find(
                        (item) => item.id === line.deviceId,
                      );
                      if (
                        !device ||
                        ["retired", "quarantined"].includes(device.status)
                      )
                        return <span>Assigned equipment unavailable</span>;
                      return <LineRegistration line={line} />;
                    })()}
                  </td>
                  <td>
                    Emergency calling not verified
                    <br />
                    <small>
                      Stored address status: {label(row.e911_status)}
                    </small>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p>No telephone numbers assigned.</p>
      )}
      {!props.customer && !props.readOnly && (
        <ExistingNumberPicker {...props} />
      )}
      <NumberPurchasePanel
        api={props.api}
        csrf={props.csrf}
        accountId={props.accountId}
        accountName={props.accountName}
        locations={props.locations}
        customer={props.customer}
        readOnly={props.readOnly}
        onSaved={props.onSaved}
        onSetup={props.onSetup}
        preferredNumber={props.preferredNumber}
      />
      <details className="cr-panel cr-editor">
        <summary>Choose an owned number</summary>
        <p className="cr-form-note">
          Choose from staff-verified inventory. A portal reservation holds the
          number for this account; it does not activate calling.
        </p>
        {inventory.error && (
          <Feedback error>
            {inventory.error}{" "}
            {inventory.data
              ? "Showing earlier owned inventory; refresh before reserving."
              : "Owned inventory is unavailable."}{" "}
            <button
              className="cr-text-button"
              disabled={inventory.loading}
              onClick={() => void inventory.reload()}
            >
              Retry owned inventory
            </button>
          </Feedback>
        )}
        {inventory.loading && (
          <p role="status">
            {inventory.data
              ? "Refreshing owned inventory…"
              : "Loading owned inventory…"}
          </p>
        )}
        <form
          className="cr-inline-form"
          onSubmit={(event) => {
            event.preventDefault();
            setSelectedNumber("");
            if (areaCode === searchArea) void inventory.reload();
            else setSearchArea(areaCode);
          }}
        >
          <Field title="Filter by North American area code">
            <input
              value={areaCode}
              onChange={(event) => {
                setAreaCode(event.target.value.replace(/\D/g, "").slice(0, 3));
                setSelectedNumber("");
              }}
              inputMode="numeric"
              pattern="[0-9]{3}"
              maxLength={3}
            />
          </Field>
          <button className="cr-button secondary" disabled={inventory.loading}>
            Search inventory
          </button>
        </form>
        {!inventory.loading && !inventory.error && !choices.length ? (
          <p>
            {searchArea
              ? "No available numbers match this area code. Clear the filter or ask Cache River to source a number."
              : "No verified numbers are available yet. Staff must add carrier inventory before a number can be selected."}
          </p>
        ) : (
          choices.length > 0 && (
            <fieldset
              className="cr-form-fields"
              disabled={inventory.loading || !!inventory.error}
            >
              <MutationForm
                {...props}
                onSaved={saved}
                path={`${base}/reserve-number`}
                button="Reserve selected number"
                success="Number reserved for this account. Carrier activation is still required."
              >
                <Field title="Available telephone number">
                  <select
                    name="numberId"
                    required
                    value={selectedNumber}
                    onChange={(event) => setSelectedNumber(event.target.value)}
                  >
                    <option value="" disabled>
                      Select a number
                    </option>
                    {choices.map((row) => (
                      <option key={row.id} value={row.id}>
                        {row.e164}
                        {row.rate_center ? ` · ${row.rate_center}` : ""}
                        {row.region ? `, ${row.region}` : ""}
                      </option>
                    ))}
                  </select>
                </Field>
              </MutationForm>
            </fieldset>
          )
        )}
        {nextCursor && (
          <button
            className="cr-button secondary"
            onClick={moreNumbers}
            disabled={inventory.loading || !!inventory.error}
          >
            Load more available numbers
          </button>
        )}
      </details>
      <details className="cr-panel cr-editor">
        <summary>Transfer existing numbers</summary>
        <MutationForm
          {...props}
          onSaved={saved}
          path={`${base}/ports`}
          button="Send port request for review"
          success="Port request saved for staff review. It has not been submitted to a carrier."
          transform={transformPort}
        >
          <PortFields />
        </MutationForm>
      </details>
      <h3>Port requests</h3>
      {portRecords.error && (
        <Feedback error>
          {portRecords.error}{" "}
          {portRecords.data
            ? "Showing earlier port requests."
            : "Port requests are unavailable."}{" "}
          <button
            className="cr-text-button"
            disabled={portRecords.loading}
            onClick={() => void portRecords.reload()}
          >
            Retry port requests
          </button>
        </Feedback>
      )}
      {portRecords.loading && (
        <p role="status">
          {portRecords.data
            ? "Refreshing port requests…"
            : "Loading port requests…"}
        </p>
      )}
      {!portRecords.loading && !portRecords.error && !ports.length && (
        <p>No port requests recorded for this account.</p>
      )}
      {ports.map((port) => (
        <details className="cr-panel cr-editor" key={port.id}>
          <summary>
            {port.requested_e164} · {label(port.review_status)}
          </summary>
          <dl className="cr-details">
            <div>
              <dt>Current carrier</dt>
              <dd>{port.losing_carrier}</dd>
            </div>
            <div>
              <dt>Account holder</dt>
              <dd>{port.authorized_name}</dd>
            </div>
            <div>
              <dt>Billing telephone</dt>
              <dd>{port.billing_telephone}</dd>
            </div>
            <div>
              <dt>Service address</dt>
              <dd>
                {["line1", "line2", "city", "region", "postalCode", "country"]
                  .map((key) => port.service_address?.[key])
                  .filter(Boolean)
                  .join(", ")}
              </dd>
            </div>
            <div>
              <dt>Requested date</dt>
              <dd>{port.requested_date?.slice(0, 10) ?? "Not requested"}</dd>
            </div>
            <div>
              <dt>Reference</dt>
              <dd>{port.id}</dd>
            </div>
          </dl>
          {port.notes && <p className="cr-message-body">{port.notes}</p>}
          {port.review_note && <Feedback>{port.review_note}</Feedback>}
          {["draft", "action_required"].includes(port.status) && (
            <>
              {!props.customer && (
                <section className="cr-port-review">
                  <h4>Staff review</h4>
                  <MutationForm
                    {...props}
                    key={`review:${port.current_revision}:${port.updated_at}`}
                    onSaved={saved}
                    path={`${base}/ports/${port.id}/review`}
                    button="Save review"
                    success="Port review updated."
                    reset={false}
                    transform={(value) => ({
                      ...value,
                      expectedRevision: port.current_revision,
                    })}
                  >
                    <Field title="Review status">
                      <select name="status" defaultValue={port.review_status}>
                        <option value="received">Awaiting review</option>
                        <option value="needs_information">
                          Needs information
                        </option>
                        <option value="ready">
                          Ready for carrier submission
                        </option>
                        <option value="cancelled">Cancel request</option>
                      </select>
                    </Field>
                    <Field title="Note to account holder">
                      <textarea
                        name="note"
                        rows={3}
                        maxLength={2000}
                        defaultValue={port.review_note}
                      />
                    </Field>
                    <p className="cr-form-note">
                      These are internal review states. Saving a review does not
                      submit or complete a carrier port.
                    </p>
                  </MutationForm>
                </section>
              )}
              <details className="cr-editor">
                <summary>Correct port details</summary>
                <MutationForm
                  {...props}
                  key={`correct:${port.current_revision}`}
                  onSaved={saved}
                  path={`${base}/ports/${port.id}/correct`}
                  button="Save corrected details"
                  success="Correction saved and returned for review."
                  transform={(value) => ({
                    ...transformPort(value),
                    expectedRevision: port.current_revision,
                  })}
                  reset={false}
                >
                  <PortFields port={port} />
                </MutationForm>
              </details>
            </>
          )}
        </details>
      ))}
    </>
  );
}

export function ExistingNumberPicker(
  props: AccountProps & { initiallyOpen?: boolean },
) {
  type Choice = {
    source: "flowroute" | "telnyx" | "twilio";
    carrierAccount: string;
    carrierLabel: string;
    number: string;
  };
  const [choices, setChoices] = useState<Choice[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState("");
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(props.initiallyOpen ?? false);
  const loading = useRef(false);
  const base = `/admin/accounts/${props.accountId}`;
  const load = useCallback(async () => {
    if (loading.current) return;
    loading.current = true;
    setBusy(true);
    setError("");
    setSelected("");
    setChoices([]);
    setWarnings([]);
    try {
      const result = await props.api<{ items: Choice[]; errors: string[] }>(
        `${base}/existing-number-pool`,
      );
      setChoices(result.items);
      setWarnings(result.errors);
      setLoaded(true);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      loading.current = false;
      setBusy(false);
    }
  }, [base, props.api]);
  useEffect(() => {
    if (props.initiallyOpen) void load();
  }, [load, props.initiallyOpen]);
  const normalize = (value: string) =>
    value.replace(/[ ()-]/g, "").toLowerCase();
  const visible = choices.filter((item) =>
    normalize(`${item.number} ${item.carrierLabel}`).includes(
      normalize(filter.trim()),
    ),
  );
  return (
    <details
      className="cr-panel cr-editor"
      open={open}
      onToggle={(event) => {
        setOpen(event.currentTarget.open);
        if (event.currentTarget.open && !loaded && !busy) void load();
      }}
    >
      <summary>Add existing number</summary>
      <p>
        Choose from your owned numbers that aren't already linked to a customer
        in this portal. A number may still be in use on another phone system.
      </p>
      <button className="cr-button secondary" disabled={busy} onClick={load}>
        Refresh existing pool
      </button>
      {busy && (
        <p role="status">Checking owned numbers and portal assignments…</p>
      )}
      {error && <Feedback error>{error}</Feedback>}
      {warnings.map((warning) => (
        <Feedback error key={warning}>
          {warning}
        </Feedback>
      ))}
      {warnings.length > 0 && (
        <p>Resolve the pool warnings before assigning a number.</p>
      )}
      {loaded &&
        !busy &&
        !error &&
        warnings.length === 0 &&
        choices.length === 0 && (
          <p>
            No unlinked, eligible numbers were found in the connected carrier
            accounts.
          </p>
        )}
      {!busy && choices.length > 0 && warnings.length === 0 && (
        <>
          <Field title="Find a number or carrier account">
            <input
              type="search"
              value={filter}
              onChange={(event) => {
                setFilter(event.target.value);
                setSelected("");
              }}
            />
          </Field>
          <MutationForm
            {...props}
            path={`${base}/existing-number`}
            button="Add number to this customer"
            success="Number linked to this customer. Existing call routing is unchanged."
            onSaved={async () => {
              await props.onSaved();
              await load();
            }}
            transform={(value) => {
              const choice = choices.find(
                (item) => item.number === value.number,
              );
              if (!choice)
                throw new Error("Choose a number from the current pool.");
              return {
                number: choice.number,
                source: choice.source,
                carrierAccount: choice.carrierAccount,
                confirmed: value.confirmed === "on",
              };
            }}
          >
            <Field title="Existing telephone number">
              <select
                name="number"
                required
                value={selected}
                onChange={(event) => setSelected(event.target.value)}
              >
                <option value="" disabled>
                  Select an owned number
                </option>
                {visible.map((choice) => (
                  <option key={choice.number} value={choice.number}>
                    {choice.number} · {choice.carrierLabel}
                  </option>
                ))}
              </select>
            </Field>
            {!visible.length && (
              <p>No matching numbers. Clear the search to see the pool.</p>
            )}
            <label className="cr-check">
              <input type="checkbox" name="confirmed" required />
              <span>
                I have confirmed that this number belongs to this customer or is
                available to assign. This only links the portal record; it does
                not change call routing or E911.
              </span>
            </label>
          </MutationForm>
        </>
      )}
    </details>
  );
}
export function PortQueue({
  api,
  onAccount,
}: Props & { onAccount: (row: Row) => void }) {
  const [ports, setPorts] = useState<Row[] | null>(null);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setError("");
    try {
      setPorts((await api<{ items: Row[] }>("/admin/ports")).items);
    } catch (cause) {
      setError((cause as Error).message);
    }
  }, [api]);
  useEffect(() => {
    void load();
  }, [load]);
  return (
    <>
      {error && (
        <Feedback error>
          {error}{" "}
          <button className="cr-text-button" onClick={load}>
            Retry
          </button>
        </Feedback>
      )}
      {!ports && !error && <p role="status">Loading port requests…</p>}
      {ports &&
        (ports.length ? (
          <div
            className="cr-table-wrap"
            tabIndex={0}
            aria-label="Port request queue"
          >
            <table>
              <thead>
                <tr>
                  <th>Telephone number</th>
                  <th>Account</th>
                  <th>Current carrier</th>
                  <th>Review status</th>
                </tr>
              </thead>
              <tbody>
                {ports.map((row) => (
                  <tr key={row.id}>
                    <td>{row.requested_e164}</td>
                    <td>
                      <button
                        className="cr-text-button"
                        onClick={() =>
                          onAccount({
                            id: row.account_id,
                            name: row.account_name,
                          })
                        }
                      >
                        {row.account_name}
                      </button>
                    </td>
                    <td>{row.losing_carrier}</td>
                    <td>{label(row.review_status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="cr-empty">
            No port requests yet. Open a customer account and select Numbers to
            submit one.
          </p>
        ))}
    </>
  );
}
